import React, { useCallback, useRef, useState } from 'react';
import type { PendingPhoto, Photo } from '../domain/listings';
import { useRepositories } from '../hooks/useRepositories';
import ConfirmDialog from './ConfirmDialog';
import ImageUpload from './ImageUpload';
import { ActionError } from './PageStates';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Trash2 } from 'lucide-react';

const MAX_PHOTOS = 20;

const inOrder = (photos: Photo[]) => [...photos].sort((a, b) => a.position - b.position);

/**
 * A listing's photos, changed one action at a time (016 research C19, Q4):
 * delete, move earlier or later (no dragging needed), and add through the
 * usual upload. Each change is saved when it's made and shown once the
 * server answers; a failure leaves the photos as they were.
 */
export default function PhotoEditor({
  listingId,
  initialPhotos,
}: {
  listingId: string;
  initialPhotos: Photo[];
}) {
  const { listings } = useRepositories();
  const [photos, setPhotos] = useState<Photo[]>(inOrder(initialPhotos));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // A fresh upload area after each add, so added photos don't linger there.
  const [uploadKey, setUploadKey] = useState(0);
  const adding = useRef(new Set<string>());
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);

  const run = async (change: () => Promise<Photo[]>) => {
    setBusy(true);
    setError(null);
    try {
      setPhotos(inOrder(await change()));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const move = (index: number, by: -1 | 1) => {
    const order = photos.map((p) => p.id);
    [order[index], order[index + by]] = [order[index + by], order[index]];
    return run(() => listings.reorderPhotos(listingId, order));
  };

  const remove = (photo: Photo) =>
    run(async () => {
      await listings.deletePhoto(listingId, photo.id);
      return photos.filter((p) => p.id !== photo.id).map((p, position) => ({ ...p, position }));
    });

  const onUploaded = useCallback(
    (pending: PendingPhoto[]) => {
      const fresh = pending.filter((p) => !adding.current.has(p.id)).map((p) => p.id);
      if (fresh.length === 0) return;
      fresh.forEach((id) => adding.current.add(id));
      run(async () => {
        const updated = await listings.addPhotos(listingId, fresh);
        setUploadKey((k) => k + 1);
        return updated;
      });
    },
    [listings, listingId],
  );

  return (
    <section aria-labelledby="photos-heading" className="space-y-4 rounded-3xl bg-card p-6">
      <div>
        <h2 id="photos-heading" className="text-xl font-semibold text-foreground">
          Photos
        </h2>
        <p className="text-sm text-muted-foreground">Photo changes are saved right away.</p>
      </div>

      {photos.length === 0 ? (
        <p className="text-sm text-muted-foreground">No photos.</p>
      ) : (
        <ul aria-label="Photos" className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {photos.map((photo, i) => {
            const n = i + 1;
            return (
              <li key={photo.id} className="space-y-2">
                <img
                  src={photo.url}
                  alt={`${n} of ${photos.length}`}
                  className="aspect-square w-full rounded-2xl bg-muted object-cover"
                />
                <div className="flex justify-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Move photo ${n} earlier`}
                    disabled={busy || i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <ArrowLeft />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Move photo ${n} later`}
                    disabled={busy || i === photos.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ArrowRight />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete photo ${n}`}
                    disabled={busy}
                    onClick={() => setPhotoToDelete(photo)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {error !== null && <ActionError error={error} />}

      {photos.length < MAX_PHOTOS && (
        <ImageUpload
          key={uploadKey}
          maxImages={MAX_PHOTOS - photos.length}
          attachedCount={photos.length}
          onImagesChange={onUploaded}
          autoUpload
        />
      )}

      <ConfirmDialog
        open={photoToDelete !== null}
        onOpenChange={(open) => !open && setPhotoToDelete(null)}
        title="Delete Photo"
        description={<p>This photo will be permanently deleted.</p>}
        confirmLabel="Delete"
        onConfirm={() => photoToDelete && remove(photoToDelete)}
      />
    </section>
  );
}
